import React from 'react'
import { FormikProps, useFormikContext } from 'formik'
import {
  FieldMetadata,
  FieldMetadataField,
} from '../../../../modules/privateOffer/types'
import clsx from 'clsx'
import {
  BoxV2,
  RadioButtonGroupMapping,
  RadioButtonType,
  RadioButtonsV2,
} from '@labrav/react-components'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRepeat } from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './PrivateOfferRenewalSection.style'
import { OffersWithErrors } from '../../../../modules/privateOffer/privateOfferCreation/types'
import { useSelector } from '../../../../store'
import { Product } from '../../../../modules/productsListing/types'
import { useFlagValue } from '@labrav/flags'

export const renewalOptions: RadioButtonGroupMapping[] = [
  {
    key: 'Yes',
    label: 'Yes',
    type: RadioButtonType.STANDARD,
  },
  {
    key: 'No',
    label: 'No',
    type: RadioButtonType.STANDARD,
  },
]
export const channelTypes: RadioButtonGroupMapping[] = [
  {
    key: 'EXTERNAL',
    label: 'Existing customer moving to AWS Marketplace',
    type: RadioButtonType.STANDARD,
    infoDescription:
      'An existing customer you have been doing business with, and the next deal is migrating to AWS Marketplace as the transaction vehicle.',
  },
  {
    key: 'AWS_MARKETPLACE',
    label: 'Existing customer on AWS Marketplace',
    type: RadioButtonType.STANDARD,
    infoDescription:
      'Buyers are getting an offer to renew an existing agreement created on AWS Marketplace.',
  },
]
export type PrivateOfferRenewalSectionProps = {
  product?: Product
}
export const PrivateOfferRenewalSection: React.FC<
  PrivateOfferRenewalSectionProps
> = ({ product }) => {
  const { classes } = useStyles()
  const { value: isRenewalPricingTermEnabled } =
    useFlagValue('renewalPricingTerm')
  const { values, errors, touched, setFieldValue } =
    useFormikContext() as FormikProps<OffersWithErrors>

  const renewalError = errors.renewal
  const renewalChannelError = errors.renewalChannel

  const hasRenewalError = renewalError && touched?.renewal
  const hasRenewalChannelError = renewalChannelError && touched?.renewalChannel
  const fieldMetadata = useSelector<FieldMetadata | undefined>(state =>
    product && product.productId
      ? state.privateOffersInCreation.currentPrivateOfferInCreation[
          product.productId
        ]?.fieldMetadata
      : undefined
  )

  const isRenewalReadOnly = (fieldMetadata?.renewal as FieldMetadataField)
    ?.readOnly
  const isRenewalHidden = (fieldMetadata?.renewal as FieldMetadataField)?.hidden

  const isRenewalChannelReadOnly = (
    fieldMetadata?.renewalChannel as FieldMetadataField
  )?.readOnly
  const isRenewalChannelHidden = (
    fieldMetadata?.renewalChannel as FieldMetadataField
  )?.hidden

  const handleSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    if (value === 'Yes') {
      await setFieldValue('renewal', true)
      await setFieldValue('renewalChannel', 'EXTERNAL')
    } else if (value === 'No') {
      await setFieldValue('renewal', false)
      await setFieldValue('renewalChannel', null)
    } else {
      await setFieldValue('renewal', '')
      await setFieldValue('renewalChannel', null)
    }
  }
  const handleSelectionRenewalType = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value

    setFieldValue('renewalChannel', value)
  }
  const checkedValue = () =>
    values.renewal ? 'Yes' : values.renewal === false ? 'No' : ''
  return (
    <div data-testid="renewal-section">
      <BoxV2
        heading={
          isRenewalPricingTermEnabled ? 'Previous contract terms' : 'Renewal'
        }
        headingIcon={
          <FontAwesomeIcon
            icon={faRepeat}
            style={{ width: '18px', height: '14px' }}
          />
        }
      >
        <div className={classes.sectionContainer}>
          {!isRenewalHidden && (
            <>
              <Typography className={classes.sectionText}>
                {isRenewalPricingTermEnabled
                  ? 'Is this offer intended to renew an existing paid subscription with this customer for the same underlying product?'
                  : 'Is this offer intended to renew an existing paid subscription with an existing customer for the same underlying product?'}
              </Typography>
              <div className={classes.widthRadio}>
                <RadioButtonsV2
                  checkedValue={{
                    key: checkedValue(),
                    value: checkedValue(),
                  }}
                  name={'renewal'}
                  handleChange={handleSelection}
                  radioButtonMappings={renewalOptions.map(option => ({
                    ...option,
                    disabled: isRenewalReadOnly || isRenewalChannelReadOnly,
                  }))}
                />
              </div>
              {hasRenewalError && (
                <Typography className={classes.partnerTypeError}>
                  {renewalError}
                </Typography>
              )}
            </>
          )}

          {values.renewal && !isRenewalChannelHidden && (
            <div data-testid="renewal-type">
              <Typography
                className={clsx(
                  classes.sectionHeading,
                  classes.renewalChannelMragin
                )}
              >
                Renewal type
              </Typography>
              <div className={classes.radioButton}>
                <RadioButtonsV2
                  checkedValue={{
                    key: 'channel',
                    value: values.renewalChannel || '',
                  }}
                  name={'renewalChannel'}
                  handleChange={handleSelectionRenewalType}
                  radioButtonMappings={channelTypes.map(option => ({
                    ...option,
                    disabled: isRenewalChannelReadOnly,
                  }))}
                  vertical
                />
              </div>
              {hasRenewalChannelError && (
                <Typography className={classes.partnerTypeError}>
                  {renewalChannelError}
                </Typography>
              )}
            </div>
          )}
        </div>
      </BoxV2>
    </div>
  )
}
